import React from 'react';
import { styled } from '@mui/material';
import { alpha, Box, darken, SxProps, useTheme } from '@mui/system';
import { IconFolder } from '@askporter/utils';
import { Icon, IconSize } from '../../../Icon';
import { FilledCircle } from '../FilledCircle';

export type DialogIconType =
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'plus'
  | 'create-asset'
  | 'create-team'
  | 'create-org'
  | 'export'
  | 'edit-time'
  | 'create-task';

const CircularLine = styled('div')<{ radius: number; opacity: number }>(({ radius, opacity }) => ({
  position: 'absolute',
  top: 24,
  left: 24,
  transform: 'translate(-50%, -50%)',
  width: `${radius}px`,
  height: `${radius}px`,
  borderRadius: '50%',
  border: '1px solid #F2F4F7',
  opacity,
}));

const CIRCULAR_RADII_OPACITY = [
  { radius: 96, opacity: 1 },
  { radius: 144, opacity: 0.8 },
  { radius: 192, opacity: 0.6 },
  { radius: 240, opacity: 0.4 },
];

const ICON_CONFIG: Record<
  DialogIconType,
  {
    backgroundColor?: string;
    folder: IconFolder;
    fillColor?: string;
    customStyles?: SxProps;
  }
> = {
  success: {
    backgroundColor: '#DCFAE6',
    folder: 'status/01-general/02-success-circle',
    fillColor: '#079455',
  },
  error: {
    backgroundColor: '#FEE4E2',
    folder: 'status/01-general/04-error-circle',
    fillColor: '#D92D20',
  },
  warning: {
    backgroundColor: '#FFFAEB',
    folder: 'status/01-general/05-information-circle',
    fillColor: '#DC6803',
    customStyles: {
      '& svg > g > g, svg > path': {
        fill: '#DC6803',
      },
    },
  },
  info: {
    backgroundColor: '#EFF6FF',
    folder: 'status/01-general/05-information-circle',
    fillColor: '#667185',
  },
  plus: {
    folder: 'actions/33-plus',
  },
  'create-asset': {
    folder: 'actions/28-create-asset',
  },
  'create-team': {
    folder: 'actions/29-create-team',
  },
  'create-org': {
    folder: 'actions/30-create-org',
  },
  export: {
    folder: 'actions/31-export',
  },
  'edit-time': {
    folder: 'actions/32-edit-time',
  },
  'create-task': {
    folder: 'actions/27-create-task',
  },
};

const DialogIcon: React.FC<{ iconType: DialogIconType }> = ({ iconType }) => {
  const theme = useTheme();
  const config = ICON_CONFIG[iconType];

  const backgroundColor = config.backgroundColor || alpha(theme.palette.primary.main, 0.2);
  const fillColor = config.fillColor || darken(theme.palette.primary.main, 0.2);

  return (
    <Box sx={{ position: 'relative' }}>
      {CIRCULAR_RADII_OPACITY.map(({ radius, opacity }) => (
        <CircularLine key={radius} radius={radius} opacity={opacity} />
      ))}
      <FilledCircle backgroundColor={backgroundColor}>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Icon
            folder={config.folder}
            size={IconSize.MD}
            fillColor={iconType === 'create-team' ? alpha(theme.palette.primary.main, 0.3) : fillColor}
            isStrokeColor={true}
            customStyles={config.customStyles}
          />
        </Box>
      </FilledCircle>
    </Box>
  );
};

export default DialogIcon;
